import axios from '@/lib/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type Ref } from 'vue'

import type { MyFeedback } from '@/modules/users/composables/useMyFeedback'

/**
 * Detalle de un reporte propio, con su línea de tiempo.
 *
 * Va aparte del listado a propósito: la bitácora sólo se pide cuando el usuario
 * abre una tarjeta. Traerla en el listado serían N consultas de eventos para
 * pintar algo que la lista no muestra.
 *
 * El backend distingue dos fallas y aquí también:
 *
 *  - 404: el reporte no es suyo (o no existe). No se le explica cuál de las dos.
 *  - 410: es suyo pero ya salió de la ventana de 48h. Es el caso del deep link
 *    viejo de la campana, que NO caduca junto con el reporte. Merece un mensaje
 *    propio: si se tratara igual que el 404, el diseño se leería como un error.
 */
const getMyFeedbackDetail = async (id: number): Promise<MyFeedback> => {
	const { data } = await axios.get<MyFeedback>(`/api/feedback/${id}`)

	return data
}

/**
 * Responde en un reporte propio.
 *
 * El backend devuelve el reporte ya actualizado —con la línea de tiempo y el
 * estatus nuevo si la réplica lo reabrió— así que no hace falta un segundo GET
 * para repintar.
 */
const replyToFeedback = async (
	id: number,
	body: string,
	version: string
): Promise<MyFeedback> => {
	const { data } = await axios.post<MyFeedback>(`/api/feedback/${id}/replies`, {
		body,
		version,
	})

	return data
}

/** Reporte actualizado que el backend adjunta al 409, si lo hay. */
const conflictPayload = (error: unknown): MyFeedback | null => {
	const response = (error as { response?: { status?: number; data?: { feedback?: MyFeedback } } })
		?.response

	return response?.status === 409 ? (response.data?.feedback ?? null) : null
}

const useMyFeedbackDetail = (id: Ref<number | null>) => {
	const queryClient = useQueryClient()

	const { data, isLoading, isError, error, refetch } = useQuery(
		['myFeedbackDetail', id],
		() => getMyFeedbackDetail(id.value as number),
		{
			// Sin id no hay nada que pedir: el detalle nace apagado y sólo arranca
			// cuando el usuario abre una tarjeta.
			enabled: computed(() => id.value !== null),
			// La bitácora de un reporte no cambia mientras se mira. Reintentar un 410
			// además no tiene sentido: la ventana no se va a reabrir sola.
			retry: false,
		}
	)

	const status = computed(() => {
		const response = (error.value as { response?: { status?: number } } | null)?.response

		return response?.status ?? null
	})

	const replyMutation = useMutation(
		(body: string) => replyToFeedback(id.value as number, body, data.value?.version ?? ''),
		{
			onSuccess: (updated) => {
				// El detalle se reemplaza con lo que contestó el POST; el listado se
				// invalida porque una réplica sobre un reporte cerrado lo reabre, y
				// entonces su badge de estatus ya no es el que está pintado.
				//
				// `id.value` y no el ref. Las dos formas funcionan —vue-query
				// desenvuelve los refs de la llave también aquí— pero eso no está en
				// su contrato público y la entrada en caché vive bajo el número. Se
				// escribe el número para no depender de esa comodidad.
				queryClient.setQueryData(['myFeedbackDetail', id.value], updated)
				queryClient.invalidateQueries(['myFeedback'])
			},
			onError: (error) => {
				// 409: el reporte cambió mientras el usuario escribía. El backend
				// adjunta el estado nuevo justo para no obligar a un segundo GET,
				// así que se repinta con eso y el siguiente intento ya lleva el
				// testigo bueno. El borrador NO se toca: lo conserva quien llamó.
				const fresh = conflictPayload(error)

				if (fresh) {
					queryClient.setQueryData(['myFeedbackDetail', id.value], fresh)
					queryClient.invalidateQueries(['myFeedback'])
				}
			},
		}
	)

	return {
		feedback: data,
		timeline: computed(() => data.value?.timeline ?? []),
		isLoading,
		isError,
		/** El reporte existía y era suyo, pero ya dejó de mostrarse. */
		isExpired: computed(() => status.value === 410),
		isNotFound: computed(() => status.value === 404),
		refetch,

		reply: replyMutation.mutateAsync,
		isReplying: replyMutation.isLoading,
	}
}

export default useMyFeedbackDetail
